import React, { useContext, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress } from '@mui/material';
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';
import { Notification } from '../../UI/Notifications';
import { DropdownSelector } from '../../UI/DropdownSelector';
import { fetchFilterOptions } from '../../../utils/api/fetchLeadFilter';
import { updateLeadField } from '../../../utils/api/Lead/updateLead';
import { handleError } from '../../../utils/custom/errorHandler';
import styles from '../../../styles/components/Modal/Lead/LeadCommentModal.module.css';

const STATUS_CONFIG = {
    sale_status: {
        endpoint: '/common/lead/filters/sale-statuses',
    },
    reten_status: {
        endpoint: '/common/lead/filters/retention-statuses',
    },
};

const translations = {
    ru: {
        sale_status: {
            title: (leadName) => `Изменить sale status для лида ${leadName}`,
            label: 'Сейл статус',
        },
        reten_status: {
            title: (leadName) => `Изменить reten status для лида ${leadName}`,
            label: 'Ретен статус',
        },
        save: 'Сохранить',
        saving: 'Сохранение...',
        cancel: 'Отмена',
        loadingError: 'Не удалось загрузить статусы.',
        updateError: 'Не удалось обновить статус.',
    },
    en: {
        sale_status: {
            title: (leadName) => `Change sale status for lead ${leadName}`,
            label: 'Sale status',
        },
        reten_status: {
            title: (leadName) => `Change reten status for lead ${leadName}`,
            label: 'Reten status',
        },
        save: 'Save',
        saving: 'Saving...',
        cancel: 'Cancel',
        loadingError: 'Failed to load statuses.',
        updateError: 'Failed to update status.',
    },
};

export const LeadStatusModal = ({ lead, role, statusKey, onClose, onSuccess }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;
    const statusTranslation = currentTranslation[statusKey];
    const statusConfig = STATUS_CONFIG[statusKey];

    const [selectedStatus, setSelectedStatus] = useState(lead?.[statusKey] || '');
    const [statusOptions, setStatusOptions] = useState([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const leadName = useMemo(() => {
        const fullName = `${lead?.first_name || ''} ${lead?.last_name || ''}`.trim();
        return fullName || `#${lead?.id ?? ''}`;
    }, [lead]);

    const selectorWidth = useMemo(() => {
        const labels = statusOptions.map((option) => String(option?.label || ''));
        const longestLabelLength = labels.reduce((max, label) => Math.max(max, label.length), 0);
        const widthInCh = Math.min(Math.max(longestLabelLength + 8, 20), 30);

        return `min(100%, ${widthInCh}ch)`;
    }, [statusOptions]);

    useEffect(() => {
        let isMounted = true;

        const loadOptions = async () => {
            setIsLoadingOptions(true);
            setErrors({});

            try {
                const options = await fetchFilterOptions(statusConfig.endpoint, langSelected);

                if (!isMounted) {
                    return;
                }

                const normalizedOptions = options.map((option) => {
                    if (typeof option === 'string') {
                        return { value: option, label: option };
                    }

                    return {
                        value: option.value !== undefined ? option.value : option.id,
                        label: option.label !== undefined ? option.label : option.name,
                    };
                });

                const matchedOption = normalizedOptions.find((option) =>
                    option.value?.toString() === lead?.[statusKey]?.toString()
                    || option.label?.toString() === lead?.[statusKey]?.toString()
                );

                if (matchedOption) {
                    setSelectedStatus(matchedOption.value);
                }

                setStatusOptions(normalizedOptions);
            } catch (error) {
                if (isMounted) {
                    setErrors({ general: currentTranslation.loadingError });
                }
            } finally {
                if (isMounted) {
                    setIsLoadingOptions(false);
                }
            }
        };

        loadOptions();

        return () => {
            isMounted = false;
        };
    }, [currentTranslation.loadingError, langSelected, statusConfig.endpoint, lead, statusKey]);

    const handleStatusChange = (_, value) => {
        setSelectedStatus(value);

        if (errors.general || errors[statusKey]) {
            setErrors({});
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setErrors({});

        try {
            await updateLeadField(lead.id, statusKey, selectedStatus, role);
            await onSuccess();
            onClose();
        } catch (error) {
            handleError(error.response?.data || error, setErrors);
            if (!error?.errors && !error?.response?.data?.errors) {
                setErrors((prev) => ({
                    ...prev,
                    general: prev.general || currentTranslation.updateError,
                }));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const statusErrorMessage = Array.isArray(errors[statusKey]) ? errors[statusKey][0] : errors[statusKey];

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div
                className={`${styles.modalContent} ${styles.statusModalContent} ${theme === 'dark' ? styles.darkContent : ''}`}
                onClick={(event) => event.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{statusTranslation.title(leadName)}</h2>
                    <button className={styles.closeButton} onClick={onClose} type="button" aria-label="Close">
                        <CloseIcon />
                    </button>
                </div>

                <div className={`${styles.modalBody} ${styles.statusModalBody}`}>
                    {(errors.general || statusErrorMessage) && (
                        <Notification message={statusErrorMessage || errors.general} type="error" />
                    )}

                    <div
                        className={`${styles.selectorGroup} ${styles.statusSelectorGroup}`}
                        style={{ width: selectorWidth }}
                    >
                        <p className={`${styles.selectorLabel} ${theme === 'dark' ? styles.darkSelectorLabel : ''}`}>
                            {statusTranslation.label}
                        </p>

                        <DropdownSelector
                            filterName={statusKey}
                            selectedValue={selectedStatus}
                            handleFilterChange={handleStatusChange}
                            options={statusOptions}
                            hasError={!!statusErrorMessage}
                            errorMessage={statusErrorMessage || ''}
                            disabled={isLoadingOptions || isSubmitting}
                        />
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button
                        type="button"
                        className={`${styles.secondaryButton} ${theme === 'dark' ? styles.darkSecondaryButton : ''}`}
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        {currentTranslation.cancel}
                    </button>
                    <button
                        type="button"
                        className={`${styles.primaryButton} ${theme === 'dark' ? styles.darkPrimaryButton : ''}`}
                        onClick={handleSubmit}
                        disabled={isSubmitting || isLoadingOptions}
                    >
                        {isSubmitting ? <CircularProgress size={18} color="inherit" /> : currentTranslation.save}
                        {isSubmitting ? currentTranslation.saving : null}
                    </button>
                </div>
            </div>
        </div>
    );
};
